'use client'

import { UploadFile } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { type DragEvent, type PropsWithChildren, useRef, useState } from 'react'

type AttachmentDropZoneProps = PropsWithChildren<{
  /** Receives the files dropped on the zone. */
  onFiles: (files: File[]) => void
  /** A disabled zone shows no drop target and takes no files, but still cancels the browser's handling of a drop. */
  disabled?: boolean
}>

const isFileDrag = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files')

/**
 * Wraps the panel so files dropped anywhere on it are attached. While a file is dragged over it, an overlay marks
 * the drop target. Drag enter and leave events fire for every child the pointer crosses, so the overlay follows a
 * count of the elements entered rather than any single event.
 */
export default function AttachmentDropZone({ onFiles, disabled = false, children }: AttachmentDropZoneProps) {
  const t = useTranslations('features.AssistantV2')
  const [isDragging, setIsDragging] = useState(false)
  const depth = useRef(0)

  const reset = () => {
    depth.current = 0
    setIsDragging(false)
  }

  const onDragEnter = (event: DragEvent) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    depth.current += 1
    setIsDragging(true)
  }

  const onDragOver = (event: DragEvent) => {
    if (!isFileDrag(event)) return
    // Cancelling dragover is what makes the zone a valid drop target.
    event.preventDefault()
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
  }

  const onDragLeave = (event: DragEvent) => {
    if (!isFileDrag(event)) return
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setIsDragging(false)
  }

  const onDrop = (event: DragEvent) => {
    if (!isFileDrag(event)) return
    // A dropped file the zone does not take would otherwise open in the tab and unload the panel.
    event.preventDefault()
    reset()
    if (disabled) return
    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) onFiles(files)
  }

  return (
    <Stack
      sx={{ flex: 1, minHeight: 0, position: 'relative' }}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
      {isDragging && !disabled && (
        <Stack
          data-testid="assistant-drop-overlay"
          spacing={1}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
            backdropFilter: 'blur(2px)',
            color: 'primary.main'
          }}
        >
          <UploadFile />
          <Typography variant="subtitle2">{t('dropFiles')}</Typography>
        </Stack>
      )}
    </Stack>
  )
}
